// The side panel. With nothing selected: subject, preview text, design. With a section selected:
// its photo, links and options. Words are edited right on the email.

import type { ComponentChildren } from 'preact';
import { BLOCK_INFO, JEN_PHOTO, type Block, type Img } from '../../shared/blocks';
import { LOOK_IDS, getLook } from '../../shared/looks';
import { safeUrl } from '../../shared/sanitize';
import type { LookId, Settings } from '../../shared/types';
import { Button, Field, Segmented, Toggle } from '../lib/ui';
import { Icon } from '../lib/icons';
import { setPath } from './paths';
import { InboxPreview } from '../parts/InboxPreview';

export interface DocFields {
  name: string;
  subject: string;
  preheader: string;
  look: LookId;
}

// ---------- email-level panel ----------

export function EmailPanel({ doc, onChange, settings }: { doc: DocFields; onChange: (patch: Partial<DocFields>, key: string) => void; settings: Settings | null }) {
  const len = doc.subject.trim().length;
  const subjHint = len === 0 ? 'Clients see this first in their inbox.' : len > 60 ? `${len} characters. Phones show about 40, so put the important words first.` : len > 45 ? `${len} characters. Good, a little long for phones.` : '';
  const addName = () => onChange({ subject: doc.subject.includes('{{first_name}}') ? doc.subject : `{{first_name}}, ${doc.subject.charAt(0).toLowerCase()}${doc.subject.slice(1)}` }, 'subject-name');
  return (
    <div class="panel-body">
      <div class="panel-tip">
        <Icon name="sparkle" size={16} />
        <span>Click any words in the email to change them. Click a photo to swap it.</span>
      </div>
      <Field label="Subject line" hint={subjHint} right={<button class="text-link small" onClick={addName}>+ First name</button>}>
        <input value={doc.subject} placeholder="Glow by the holidays" onInput={(e) => onChange({ subject: e.currentTarget.value }, 'subject')} />
      </Field>
      <Field label="Preview text">
        <input value={doc.preheader} placeholder="Book in November for December parties." onInput={(e) => onChange({ preheader: e.currentTarget.value }, 'preheader')} />
      </Field>
      <div class="field">
        <span class="field-label">In her inbox</span>
        <InboxPreview fromName={settings?.from_name || 'LaVir Med Spa'} subject={doc.subject} preheader={doc.preheader} />
      </div>
      <div class="field">
        <span class="field-label">Design</span>
        <div class="look-swatches">
          {LOOK_IDS.map((id) => {
            const L = getLook(id);
            return (
              <button key={id} class={`swatch ${doc.look === id ? 'on' : ''}`} onClick={() => onChange({ look: id }, 'look')} title={L.description}>
                <span class="swatch-chip" style={{ background: L.paper, borderColor: L.line }}>
                  <i style={{ background: L.buttonBg }} />
                  <em style={{ fontFamily: L.headFont, color: L.heading }}>Aa</em>
                </span>
                <span>{L.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      <Field label="Name in your list" hint="Only you see this.">
        <input value={doc.name} onInput={(e) => onChange({ name: e.currentTarget.value }, 'name')} />
      </Field>
      {settings && (
        <div class="from-line">
          <span class="field-label">From</span>
          <div>
            {settings.from_name} <span class="muted">&lt;{settings.from_email}&gt;</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- section panel ----------

interface BlockProps {
  block: Block;
  settings: Settings | null;
  onChange: (b: Block, key: string) => void;
  onPickImage: (path: string) => void;
  onAction: (act: 'up' | 'down' | 'copy' | 'delete') => void;
  onDone: () => void;
}

export function BlockPanel({ block, settings, onChange, onPickImage, onAction, onDone }: BlockProps) {
  const info = BLOCK_INFO.find((x) => x.type === block.type)!;
  const set = (path: string, value: unknown, key = path) => onChange(setPath(block, path, value), `${block.id}:${key}`);

  return (
    <div class="panel-body">
      <div class="panel-title">
        <button class="text-link" onClick={onDone}>
          <Icon name="back" size={16} /> Email settings
        </button>
        <h3>{info.label}</h3>
      </div>
      <Fields block={block} set={set} settings={settings} onPickImage={onPickImage} onChange={onChange} />
      <div class="block-actions">
        <Button size="s" icon="up" onClick={() => onAction('up')}>
          Up
        </Button>
        <Button size="s" icon="dn" onClick={() => onAction('down')}>
          Down
        </Button>
        <Button size="s" icon="copy" onClick={() => onAction('copy')}>
          Copy
        </Button>
        <Button size="s" kind="quiet" icon="trash" class="danger-text" onClick={() => onAction('delete')}>
          Delete
        </Button>
      </div>
    </div>
  );
}

function Fields({
  block: b,
  set,
  settings,
  onPickImage,
  onChange,
}: {
  block: Block;
  set: (path: string, v: unknown, key?: string) => void;
  settings: Settings | null;
  onPickImage: (path: string) => void;
  onChange: (b: Block, key: string) => void;
}) {
  const photo = (img: Img, path: string, label = 'Photo') => <PhotoField img={img} label={label} onPick={() => onPickImage(path)} onAlt={(v) => set(`${path}.alt`, v)} />;
  const link = (value: string, path: string, label = 'Link goes to', optional = false) => <UrlField label={label} value={value} settings={settings} optional={optional} onChange={(v) => set(path, v)} />;
  const hintWords = <p class="panel-hint">Click the words in the email to change them.</p>;

  switch (b.type) {
    case 'hero':
      return (
        <>
          {photo(b.image, 'image')}
          <ToggleRow label="Words on top of the photo" on={b.overlay} onChange={(v) => set('overlay', v)} />
          {hintWords}
        </>
      );
    case 'heading':
      return (
        <>
          <Field label="Size">
            <Segmented label="Size" value={b.size} onChange={(v) => set('size', v)} options={[{ value: 'l', label: 'Large' }, { value: 'm', label: 'Medium' }]} />
          </Field>
          {hintWords}
        </>
      );
    case 'text':
      return (
        <>
          <p class="panel-hint">Click the text in the email to change it. Select words to make them bold or add a link.</p>
          <p class="panel-hint">
            Type <code>{'{{first_name}}'}</code> to greet each person by name.
          </p>
        </>
      );
    case 'image':
      return (
        <>
          {photo(b.image, 'image')}
          {link(b.link, 'link', 'When tapped, go to', true)}
        </>
      );
    case 'button':
      return (
        <>
          {link(b.url, 'url', 'Button goes to')}
          {hintWords}
        </>
      );
    case 'split':
      return (
        <>
          {photo(b.image, 'image')}
          <Field label="Photo side">
            <Segmented label="Photo side" value={b.imageSide} onChange={(v) => set('imageSide', v)} options={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }]} />
          </Field>
          {link(b.url, 'url', 'Link goes to')}
          {hintWords}
        </>
      );
    case 'tiles':
      return (
        <>
          {b.items.map((t, i) => (
            <div class="sub-group" key={i}>
              <h4>Photo {i + 1}</h4>
              {photo(t.image, `items.${i}.image`, 'Photo')}
              {link(t.url, `items.${i}.url`)}
            </div>
          ))}
        </>
      );
    case 'timeline':
      return (
        <>
          <RowList
            items={b.rows.map((r) => r.heading || r.label || 'Row')}
            onRemove={(i) => onChange({ ...b, rows: b.rows.filter((_, j) => j !== i) }, `${b.id}:rows`)}
            onAdd={() => onChange({ ...b, rows: [...b.rows, { label: 'When', heading: 'Treatment', text: 'A short note.' }] }, `${b.id}:rows`)}
            addLabel="Add a row"
          />
          {hintWords}
        </>
      );
    case 'prices':
      return (
        <>
          {b.groups.map((g, gi) => (
            <div class="sub-group" key={gi}>
              <h4>{g.name || `Group ${gi + 1}`}</h4>
              <RowList
                items={g.items.map((it) => `${it.name} ${it.price}`.trim())}
                onRemove={(i) => {
                  const groups = b.groups.map((x, j) => (j === gi ? { ...x, items: x.items.filter((_, k) => k !== i) } : x)).filter((x) => x.items.length);
                  onChange({ ...b, groups }, `${b.id}:groups`);
                }}
                onAdd={() => onChange({ ...b, groups: b.groups.map((x, j) => (j === gi ? { ...x, items: [...x.items, { name: 'Treatment', was: '', price: '$0', unit: '' }] } : x)) }, `${b.id}:groups`)}
                addLabel="Add a price"
              />
            </div>
          ))}
          <Button size="s" icon="plus" onClick={() => onChange({ ...b, groups: [...b.groups, { name: 'New group', items: [{ name: 'Treatment', was: '', price: '$0', unit: '' }] }] }, `${b.id}:groups`)}>
            Add a group
          </Button>
          <p class="panel-hint">Click names and prices in the email to change them. Leave "was" empty if there is no sale price.</p>
        </>
      );
    case 'offer':
      return (
        <>
          {link(b.url, 'url', 'Button goes to')}
          <Field label="Style">
            <Segmented label="Style" value={b.style} onChange={(v) => set('style', v)} options={[{ value: 'dark', label: 'Dark' }, { value: 'card', label: 'Light card' }]} />
          </Field>
          {hintWords}
        </>
      );
    case 'quote':
      return (
        <>
          <ToggleRow label="Show a photo" on={!!b.photo} onChange={(v) => set('photo', v ? JEN_PHOTO : null)} />
          {b.photo && photo(b.photo, 'photo')}
          {hintWords}
        </>
      );
    case 'divider':
      return <p class="panel-hint">A thin line between sections.</p>;
    case 'spacer':
      return (
        <Field label="Space">
          <Segmented label="Space" value={b.size} onChange={(v) => set('size', v)} options={[{ value: 's', label: 'Small' }, { value: 'm', label: 'Medium' }, { value: 'l', label: 'Large' }]} />
        </Field>
      );
  }
}

function ToggleRow({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <div class="toggle-row">
      <span>{label}</span>
      <Toggle on={on} onChange={onChange} label={label} />
    </div>
  );
}

function PhotoField({ img, label, onPick, onAlt }: { img: Img; label: string; onPick: () => void; onAlt: (v: string) => void }) {
  return (
    <div class="photo-field">
      <span class="field-label">{label}</span>
      <button class="photo-swap" onClick={onPick}>
        {img.src ? <img src={img.src} alt="" /> : <Icon name="image" size={24} />}
        <span>
          <Icon name="image" size={16} /> Change photo
        </span>
      </button>
      <Field label="Describe the photo" hint="Shows if a client's email app hides photos.">
        <input value={img.alt} placeholder="Woman with glowing skin" onInput={(e) => onAlt(e.currentTarget.value)} />
      </Field>
    </div>
  );
}

function UrlField({ label, value, onChange, settings, optional }: { label: string; value: string; onChange: (v: string) => void; settings: Settings | null; optional?: boolean }) {
  const ok = !value || !!safeUrl(value);
  const quick: [string, string][] = settings
    ? (
        [
          ['Booking', settings.booking_url],
          ['Website', settings.website],
          ['Instagram', settings.instagram],
        ] as [string, string][]
      ).filter(([, u]) => !!u)
    : [];
  return (
    <Field label={label} error={!ok ? 'Start with https:// (copy the address from your browser).' : !value && !optional ? 'Add where this goes.' : null}>
      <input type="url" inputMode="url" value={value} placeholder="https://" onInput={(e) => onChange(e.currentTarget.value.trim())} />
      {quick.length > 0 && (
        <span class="quick-links">
          {quick.map(([name, url]) => (
            <button key={name} type="button" class={`quick ${value === url ? 'on' : ''}`} onClick={() => onChange(url)}>
              {name}
            </button>
          ))}
        </span>
      )}
    </Field>
  );
}

function RowList({ items, onRemove, onAdd, addLabel }: { items: string[]; onRemove: (i: number) => void; onAdd: () => void; addLabel: string }) {
  return (
    <div class="row-list">
      {items.map((t, i) => (
        <div class="row-item" key={i}>
          <span>{t}</span>
          <button class="icon-btn" aria-label="Remove" title="Remove" onClick={() => onRemove(i)} disabled={items.length <= 1}>
            <Icon name="x" size={16} />
          </button>
        </div>
      ))}
      <Button size="s" icon="plus" onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  );
}

export function PanelSection({ children }: { children: ComponentChildren }) {
  return <div class="panel-section">{children}</div>;
}
